/**
 * EncryptedChildInfoCard — Phase 3 Layer B UI.
 *
 * Lives inside Manage Board → Parental Settings. Lets the parent enter
 * (and re-enter after a password reset) the child's real name, birth
 * date, and school contact. All three fields are encrypted client-side
 * with the parent's password-derived key and stored as an opaque
 * envelope (`profile.encrypted_pii`) on the server.
 *
 * Render states:
 *   - locked: no PII key cached → ask the parent to sign in again
 *   - unencrypted yet: form starts empty; parent fills in and saves
 *   - encrypted, decryptable: form pre-fills with decrypted values
 *   - encrypted, NOT decryptable (after password reset): banner + form
 *     starts empty (parent re-enters; old envelope gets overwritten)
 */
import { useEffect, useState } from "react";
import { Lock, Save, AlertTriangle, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  hasPiiKey, encryptPii, decryptPii,
} from "@/lib/profileCrypto";

const STATES = {
  LOCKED: "locked",
  EMPTY: "empty",
  PRESENT: "present",
  STALE: "stale",  // present but couldn't decrypt
};

export default function EncryptedChildInfoCard({ profile, onSaved }) {
  const { user } = useAuth();
  const [state, setState] = useState(STATES.LOCKED);
  const [childName, setChildName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [schoolContact, setSchoolContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedFlag, setSavedFlag] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const keyed = user?.id ? hasPiiKey(user.id) : false;
      if (!keyed) {
        if (!cancelled) setState(STATES.LOCKED);
        return;
      }
      const env = profile?.encrypted_pii;
      if (!env) {
        if (!cancelled) setState(STATES.EMPTY);
        return;
      }
      const pii = await decryptPii(env);
      if (cancelled) return;
      if (!pii) {
        setState(STATES.STALE);
        setChildName("");
        setBirthDate("");
        setSchoolContact("");
        return;
      }
      setChildName(pii.name || "");
      setBirthDate(pii.birth_date || "");
      setSchoolContact(pii.school_contact || "");
      setState(STATES.PRESENT);
    })();
    return () => { cancelled = true; };
  }, [profile?.id, profile?.encrypted_pii, user?.id]);

  const save = async () => {
    setBusy(true);
    setError("");
    setSavedFlag(false);
    try {
      const envelope = await encryptPii({
        name: childName.trim() || null,
        birth_date: birthDate.trim() || null,
        school_contact: schoolContact.trim() || null,
      });
      if (!envelope) {
        throw new Error("Could not encrypt — please sign out and sign in again.");
      }
      await api.put(`/profiles/${profile.id}`, {
        ...profile,
        encrypted_pii: envelope,
      });
      setSavedFlag(true);
      setState(STATES.PRESENT);
      if (onSaved) onSaved();
      setTimeout(() => setSavedFlag(false), 2500);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Erase the encrypted child info from this board? You can re-enter it any time.")) return;
    setBusy(true);
    setError("");
    try {
      await api.put(`/profiles/${profile.id}`, { ...profile, encrypted_pii: null });
      setChildName("");
      setBirthDate("");
      setSchoolContact("");
      setState(STATES.EMPTY);
      if (onSaved) onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Could not clear.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="brutal-card p-4 bg-white mb-4"
      data-testid="encrypted-child-info-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <Lock size={18} aria-hidden />
        <h3 className="display text-lg font-extrabold">Private child info</h3>
      </div>
      <p className="text-sm text-[color:var(--ink)]/70 mb-3 leading-snug">
        Encrypted in your browser before it ever reaches us. Only this
        device — signed in as you — can read it.
        <strong className="block mt-1">We can't see this. We can't recover it.</strong>
      </p>

      {state === STATES.LOCKED && (
        <div
          className="brutal-card p-3 bg-[color:var(--cream)] text-sm"
          data-testid="encrypted-child-info-locked"
        >
          Sign in again to unlock this section. The encryption key is set
          when you enter your password — closing the browser clears it for
          your safety.
        </div>
      )}

      {state === STATES.STALE && (
        <div
          className="brutal-card p-3 bg-amber-50 text-sm text-amber-900 mb-3 flex items-start gap-2"
          data-testid="encrypted-child-info-stale"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            Your password was reset — the old encrypted info can't be read
            anymore. Type the details again and save to re-encrypt.
          </span>
        </div>
      )}

      {state !== STATES.LOCKED && (
        <>
          <label className="block mb-3">
            <span className="display font-semibold text-sm mb-1 block">Child's real name</span>
            <input
              className="brutal-input w-full"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              placeholder="(stays on your device, encrypted)"
              maxLength={80}
              data-testid="encrypted-child-info-name"
            />
          </label>
          <label className="block mb-3">
            <span className="display font-semibold text-sm mb-1 block">Birth date</span>
            <input
              type="date"
              className="brutal-input w-full"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              data-testid="encrypted-child-info-birth"
            />
          </label>
          <label className="block mb-3">
            <span className="display font-semibold text-sm mb-1 block">School / classroom contact</span>
            <input
              className="brutal-input w-full"
              value={schoolContact}
              onChange={(e) => setSchoolContact(e.target.value)}
              placeholder="e.g. Ms. Carter, Rm 4"
              maxLength={200}
              data-testid="encrypted-child-info-school"
            />
          </label>

          {error && (
            <div
              className="brutal-card bg-red-50 p-2 text-sm text-red-700 mb-3"
              data-testid="encrypted-child-info-error"
            >
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 justify-end">
            {state === STATES.PRESENT && (
              <button
                type="button"
                onClick={clearAll}
                disabled={busy}
                className="pill-btn"
                data-testid="encrypted-child-info-clear"
              >
                <Trash2 size={14} /> Erase
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="pill-btn primary"
              data-testid="encrypted-child-info-save"
            >
              <Save size={14} /> {busy ? "Encrypting…" : savedFlag ? "Saved ✓" : "Save"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
